import { activeTabKey, sectionTabs } from './sectionTabs';
import type { Section } from '../sections/types';
import type { Task } from '../tasks/types';

/**
 * The section strip above the unified board.
 *
 * It writes filters.sectionId — the very field the toolbar's Section dropdown
 * writes — rather than holding a selection of its own. That is the whole
 * design: two controls over one piece of state cannot disagree, and the choice
 * rides into a saved view with no new plumbing, survives the trip to List View
 * and back, and clears with the toolbar's Clear button like any other filter.
 *
 * Counts come from the UNFILTERED task list on purpose. A tab that reported
 * how many tasks it would show after the other filters were applied would read
 * zero for most sections whenever anything else was set, and the strip would
 * look broken rather than filtered.
 */
export function SectionFilterTabs({
  sections,
  tasks,
  sectionId,
  onChange,
}: {
  sections: Section[];
  /** Every top-level task in the project, before filtering. */
  tasks: Task[];
  /** undefined is All Tasks; null is the real "no section" choice. */
  sectionId: string | null | undefined;
  onChange: (next: string | null | undefined) => void;
}) {
  const tabs = sectionTabs(sections, tasks);
  const active = activeTabKey(sectionId);

  return (
    <div className="section-tabs" role="group" aria-label="Filter board by section">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className="section-tab"
          aria-pressed={tab.key === active}
          // Spelled out, because the visible name and the count are separate
          // elements and the computed name would otherwise run them together
          // as "My Tasks1" — read aloud, that is a different section.
          aria-label={`${tab.name} — ${String(tab.count)} ${tab.count === 1 ? 'task' : 'tasks'}`}
          onClick={() => onChange(tab.sectionId)}
        >
          <span className="section-tab-name">{tab.name}</span>
          <span className="section-tab-count">{tab.count}</span>
        </button>
      ))}
    </div>
  );
}
